// src/components/TerminateEmployeeDialog.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  Box
} from '@mui/material';
import { doc, updateDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { tryResolveTenantId } from '../utils/tenant';

const TERMINATION_REASONS = [
  'Voluntary Resignation',
  'Performance Issues',
  'Attendance Issues',
  'Policy Violation',
  'Position Eliminated',
  'End of Contract',
  'Misconduct',
  'No Call/No Show',
  'Better Opportunity',
  'Personal Reasons',
  'Retirement',
  'Other'
];

export default function TerminateEmployeeDialog({ 
  open, 
  onClose, 
  employee, 
  onTerminated 
}) {
  const [formData, setFormData] = useState({
    reason: '',
    customReason: '',
    notes: '',
    isRehireable: true,
    finalWorkDate: new Date().toISOString().split('T')[0],
    terminatedBy: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleSubmit = async () => {
    if (!formData.reason) {
      setError('Termination reason is required');
      return;
    }

    if (formData.reason === 'Other' && !formData.customReason) {
      setError('Please specify the custom reason');
      return;
    }

    if (!formData.terminatedBy) {
      setError('Terminated by field is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const currentUser = JSON.parse(localStorage.getItem('adminUser'));
      const terminationReason = formData.reason === 'Other' ? formData.customReason : formData.reason;

  // Update employee status to terminated (tenant-scoped)
  const tenantId = await tryResolveTenantId();
  if (!tenantId) throw new Error('Tenant not resolved');
  const employeeRef = doc(db, `tenants/${tenantId}/employees`, employee.id);
  await updateDoc(employeeRef, {
        'employment.currentStatus': 'Terminated',
        'employment.terminationDate': formData.finalWorkDate,
        'employment.terminationReason': terminationReason,
        'employment.isRehireable': formData.isRehireable,
        'employment.terminatedBy': formData.terminatedBy,
        'employment.terminationNotes': formData.notes,
        'employment.lastModified': serverTimestamp(),
        'employment.lastModifiedBy': currentUser?.name || 'Unknown'
      });

      // Create termination record for archive
      await addDoc(collection(db, `tenants/${tenantId}/terminations`), {
        employeeId: employee.id,
        employeeName: `${employee.firstName} ${employee.lastName}`,
        employeeEmail: employee.email,
        terminationDate: formData.finalWorkDate,
        reason: terminationReason,
        notes: formData.notes,
        isRehireable: formData.isRehireable,
        terminatedBy: formData.terminatedBy,
        terminatedByUser: currentUser?.name || 'Unknown',
        createdAt: serverTimestamp()
      });

      onTerminated();
      onClose();
    } catch (err) {
      console.error('Error terminating employee:', err);
      setError('Failed to terminate employee. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      reason: '',
      customReason: '',
      notes: '',
      isRehireable: true,
      finalWorkDate: new Date().toISOString().split('T')[0],
      terminatedBy: ''
    });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Terminate Employee: {employee?.firstName} {employee?.lastName}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Alert severity="warning">
            This action will terminate the employee and move them to the archive. 
            They will no longer be able to access the system.
          </Alert>

          {error && (
            <Alert severity="error">{error}</Alert>
          )}

          <TextField
            label="Final Work Date"
            type="date"
            value={formData.finalWorkDate}
            onChange={handleChange('finalWorkDate')}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <FormControl fullWidth required>
            <InputLabel>Termination Reason</InputLabel>
            <Select
              value={formData.reason}
              onChange={handleChange('reason')}
              label="Termination Reason"
            >
              {TERMINATION_REASONS.map(reason => (
                <MenuItem key={reason} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {formData.reason === 'Other' && (
            <TextField
              label="Custom Reason"
              value={formData.customReason}
              onChange={handleChange('customReason')}
              fullWidth
              required
              multiline
              rows={2}
            />
          )}

          <TextField
            label="Terminated By (Name/Title)"
            value={formData.terminatedBy}
            onChange={handleChange('terminatedBy')}
            fullWidth
            required
            placeholder="e.g., John Smith - Store Manager"
          />

          <TextField
            label="Additional Notes"
            value={formData.notes}
            onChange={handleChange('notes')}
            fullWidth
            multiline
            rows={3}
            placeholder="Any additional details about the termination..."
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isRehireable}
                onChange={handleCheckboxChange('isRehireable')}
              />
            }
            label="Employee is eligible for rehire"
          />

          <Typography variant="body2" color="text.secondary">
            This information will be stored in the employee archive for HR records.
            Only owners can permanently delete employee records from the archive.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="warning"
          disabled={loading}
        >
          {loading ? 'Terminating...' : 'Terminate Employee'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
